/**
 * Small haptic ticks for touch gestures.
 *
 * Android Chrome honours `navigator.vibrate`; iOS Safari has no vibration API, so there the
 * motion of the gesture itself is the feedback. Never fires for a mouse or trackpad — a desk
 * that buzzes on every click would be worse than none.
 */
const canBuzz = (): boolean =>
  typeof navigator !== 'undefined' &&
  typeof navigator.vibrate === 'function' &&
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(pointer: coarse)').matches;

const buzz = (pattern: number | number[]) => {
  try {
    if (canBuzz()) navigator.vibrate(pattern);
  } catch {
    /* some WebViews throw when vibration is blocked — the gesture still works */
  }
};

export const haptic = {
  /** A threshold was crossed (row opened, sheet about to close). */
  tick: () => buzz(8),
  /** An action was committed (full swipe, pull-to-refresh released). */
  commit: () => buzz(16),
  /** Something was refused or destructive. */
  warn: () => buzz([12, 40, 12]),
};

/** Whether the element (or the document) lays out right-to-left. */
export const isRtl = (el?: Element | null): boolean => {
  if (el && typeof window !== 'undefined') return window.getComputedStyle(el).direction === 'rtl';
  return typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
};
